import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Plus, Check, X, Eye, DollarSign, Calendar, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { payrollService } from '../../services/payroll.service';
import { userService } from '../../services/user.service';
import { PayrollStatus, PayrollRecord, AllowanceItem, DeductionItem } from '../../types';

const payrollSchema = z.object({
  userId: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(1, 'User is required')
  ),
  payrollYear: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(2020, 'Year is required')
  ),
  payrollMonth: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(1).max(12, 'Month is required')
  ),
  basicSalary: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().optional()
  ),
  notes: z.string().optional(),
});

type PayrollFormData = z.output<typeof payrollSchema>;

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const PayrollPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState<PayrollRecord | null>(null);
  const [allowances, setAllowances] = useState<AllowanceItem[]>([]);
  const [deductions, setDeductions] = useState<DeductionItem[]>([]);
  const [newAllowanceName, setNewAllowanceName] = useState('');
  const [newAllowanceAmount, setNewAllowanceAmount] = useState('');
  const [newDeductionName, setNewDeductionName] = useState('');
  const [newDeductionAmount, setNewDeductionAmount] = useState('');
  
  // Filter states
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState<number | ''>('');

  const { data: payrolls, isLoading } = useQuery({
    queryKey: ['payrolls'],
    queryFn: payrollService.getAll,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: userService.getAll,
  });

  const form = useForm<PayrollFormData>({
    // @ts-expect-error - Zod coerce types work correctly at runtime
    resolver: zodResolver(payrollSchema),
    defaultValues: {
      payrollYear: new Date().getFullYear(),
      payrollMonth: new Date().getMonth() + 1,
    },
  });

  const { register, handleSubmit, reset, formState: { errors } } = form;

  const createMutation = useMutation({
    mutationFn: (data: PayrollFormData) => payrollService.create({
      ...data,
      allowances,
      deductions,
    }),
    onSuccess: () => {
      toast.success('Payroll record created successfully');
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
      handleCloseModal();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to create payroll record');
    },
  });

  const submitMutation = useMutation({
    mutationFn: payrollService.submit,
    onSuccess: () => {
      toast.success('Payroll submitted for approval');
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to submit payroll');
    },
  });

  const approveMutation = useMutation({
    mutationFn: payrollService.approve,
    onSuccess: () => {
      toast.success('Payroll approved');
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to approve payroll');
    },
  });

  const payMutation = useMutation({
    mutationFn: payrollService.markAsPaid,
    onSuccess: () => {
      toast.success('Payroll marked as paid');
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to mark payroll as paid');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: payrollService.delete,
    onSuccess: () => {
      toast.success('Payroll record deleted');
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to delete payroll record');
    },
  });

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAllowances([]);
    setDeductions([]);
    setNewAllowanceName('');
    setNewAllowanceAmount('');
    setNewDeductionName('');
    setNewDeductionAmount('');
    reset();
  };

  const handleAddAllowance = () => {
    if (newAllowanceName && newAllowanceAmount) {
      setAllowances([...allowances, { name: newAllowanceName, amount: Number(newAllowanceAmount) }]);
      setNewAllowanceName('');
      setNewAllowanceAmount('');
    }
  };

  const handleRemoveAllowance = (index: number) => {
    setAllowances(allowances.filter((_, i) => i !== index));
  };

  const handleAddDeduction = () => {
    if (newDeductionName && newDeductionAmount) {
      setDeductions([...deductions, { name: newDeductionName, amount: Number(newDeductionAmount) }]);
      setNewDeductionName('');
      setNewDeductionAmount('');
    }
  };

  const handleRemoveDeduction = (index: number) => {
    setDeductions(deductions.filter((_, i) => i !== index));
  };

  const onSubmit = (data: PayrollFormData) => {
    createMutation.mutate(data);
  };

  const handleView = (payroll: PayrollRecord) => {
    setSelectedPayroll(payroll);
    setIsViewModalOpen(true);
  };

  const handleDelete = (payroll: PayrollRecord) => {
    if (payroll.status === PayrollStatus.PAID) {
      toast.error('Cannot delete paid payroll records');
      return;
    }
    if (window.confirm('Are you sure you want to delete this payroll record?')) {
      deleteMutation.mutate(payroll.id);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusVariant = (status: PayrollStatus) => {
    switch (status) {
      case PayrollStatus.DRAFT:
        return 'default';
      case PayrollStatus.PENDING_APPROVAL:
        return 'warning';
      case PayrollStatus.APPROVED:
        return 'info';
      case PayrollStatus.PAID:
        return 'success';
      case PayrollStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  // Calculate totals
  const totalAllowances = allowances.reduce((sum, a) => sum + a.amount, 0);
  const totalDeductions = deductions.reduce((sum, d) => sum + d.amount, 0);
  const { watch } = form;
  const watchedBasicSalary = watch('basicSalary');
  const basicSalary = Number(watchedBasicSalary) || 0;
  const grossPay = basicSalary + totalAllowances;
  const netPay = grossPay - totalDeductions;

  // Filter payrolls
  const filteredPayrolls = payrolls?.filter((p) => {
    const matchesYear = p.payrollYear === yearFilter;
    const matchesMonth = monthFilter === '' || p.payrollMonth === monthFilter;
    return matchesYear && matchesMonth;
  }) || [];

  // Summary calculations
  const totalNetPay = filteredPayrolls.reduce((sum, p) => sum + p.netPay, 0);
  const paidCount = filteredPayrolls.filter(p => p.status === PayrollStatus.PAID).length;
  const pendingCount = filteredPayrolls.filter(p => p.status !== PayrollStatus.PAID).length;

  // Get active users for payroll
  const activeUsers = users?.filter(u => u.active) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal">Payroll Management</h1>
          <p className="text-gray-600">Manage payroll records</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus size={16} className="mr-2" />
          Create Payroll
        </Button>
      </div>

      {/* Removed tab navigation - employees are now managed in Users page */}

      <>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Users size={16} />
              <span className="text-sm">Total Records</span>
            </div>
            <p className="text-2xl font-bold">{filteredPayrolls.length}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <DollarSign size={16} />
              <span className="text-sm">Total Net Pay</span>
            </div>
            <p className="text-2xl font-bold text-primary">{formatCurrency(totalNetPay)}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <Check size={16} />
              <span className="text-sm">Paid</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{paidCount}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-yellow-600 mb-1">
              <Calendar size={16} />
              <span className="text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value === '' ? '' : Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Months</option>
              {months.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Payroll Table */}
      <Card title="Payroll Records">
        {isLoading && <div className="text-center py-8 text-gray-500">Loading payroll records...</div>}

        {!isLoading && filteredPayrolls.length === 0 && (
          <div className="text-center py-8 text-gray-500">No payroll records found</div>
        )}

        {!isLoading && filteredPayrolls.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payroll #</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Basic Salary</TableHead>
                  <TableHead className="text-right">Allowances</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayrolls.map((payroll) => (
                  <TableRow key={payroll.id}>
                    <TableCell className="font-medium">{payroll.payrollNumber}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{payroll.userName}</p>
                        <p className="text-xs text-gray-500">{payroll.userEmail || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell>{payroll.payrollPeriod}</TableCell>
                    <TableCell className="text-right">{formatCurrency(payroll.basicSalary)}</TableCell>
                    <TableCell className="text-right text-green-600">+{formatCurrency(payroll.totalAllowances)}</TableCell>
                    <TableCell className="text-right text-red-600">-{formatCurrency(payroll.totalDeductions)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(payroll.netPay)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(payroll.status)}>{payroll.status.replace('_', ' ')}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleView(payroll)}
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        {payroll.status === PayrollStatus.DRAFT && (
                          <>
                            <button
                              onClick={() => submitMutation.mutate(payroll.id)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Submit for Approval"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(payroll)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                        {payroll.status === PayrollStatus.PENDING_APPROVAL && (
                          <button
                            onClick={() => approveMutation.mutate(payroll.id)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Approve"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {payroll.status === PayrollStatus.APPROVED && (
                          <button
                            onClick={() => payMutation.mutate(payroll.id)}
                            className="p-1 text-primary hover:bg-blue-50 rounded"
                            title="Mark as Paid"
                          >
                            <DollarSign size={16} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      </>

      {/* Create Payroll Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Create Payroll Record"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal} disabled={createMutation.isPending}>
              Cancel
            </Button>
            {/* @ts-expect-error - Form types work correctly at runtime */}
            <Button onClick={handleSubmit(onSubmit)} loading={createMutation.isPending}>
              Create Payroll
            </Button>
          </>
        }
      >
        {/* @ts-expect-error - Form types work correctly at runtime */}
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User <span className="text-red-500">*</span>
              </label>
              <select
                {...register('userId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select User</option>
                {activeUsers?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} ({u.username})
                  </option>
                ))}
              </select>
              {errors.userId && <p className="text-xs text-red-500 mt-1">{errors.userId.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                {...register('payrollYear')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {errors.payrollYear && <p className="text-xs text-red-500 mt-1">{errors.payrollYear.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Month <span className="text-red-500">*</span>
              </label>
              <select
                {...register('payrollMonth')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              {errors.payrollMonth && <p className="text-xs text-red-500 mt-1">{errors.payrollMonth.message}</p>}
            </div>
          </div>

          <Input
            label="Basic Salary"
            type="number"
            step="0.01"
            placeholder="0"
            error={errors.basicSalary?.message}
            {...register('basicSalary')}
          />

          {/* Allowances Section */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-3">Allowances</h4>
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="Allowance name"
                value={newAllowanceName}
                onChange={(e) => setNewAllowanceName(e.target.value)}
              />
              <Input
                type="number"
                placeholder="Amount"
                value={newAllowanceAmount}
                onChange={(e) => setNewAllowanceAmount(e.target.value)}
              />
              <Button type="button" variant="secondary" onClick={handleAddAllowance}>
                <Plus size={16} />
              </Button>
            </div>
            {allowances.length > 0 && (
              <div className="space-y-2">
                {allowances.map((a, i) => (
                  <div key={i} className="flex items-center justify-between bg-green-50 p-2 rounded">
                    <span>{a.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 font-medium">+{formatCurrency(a.amount)}</span>
                      <button type="button" onClick={() => handleRemoveAllowance(i)} className="text-red-500">
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deductions Section */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-3">Deductions</h4>
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="Deduction name"
                value={newDeductionName}
                onChange={(e) => setNewDeductionName(e.target.value)}
              />
              <Input
                type="number"
                placeholder="Amount"
                value={newDeductionAmount}
                onChange={(e) => setNewDeductionAmount(e.target.value)}
              />
              <Button type="button" variant="secondary" onClick={handleAddDeduction}>
                <Plus size={16} />
              </Button>
            </div>
            {deductions.length > 0 && (
              <div className="space-y-2">
                {deductions.map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-red-50 p-2 rounded">
                    <span>{d.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-red-600 font-medium">-{formatCurrency(d.amount)}</span>
                      <button type="button" onClick={() => handleRemoveDeduction(i)} className="text-red-500">
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Basic Salary</span>
              <span className="font-medium">{formatCurrency(basicSalary)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Allowances</span>
              <span className="font-medium text-green-600">+{formatCurrency(totalAllowances)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-gray-600">Gross Pay</span>
              <span className="font-medium">{formatCurrency(grossPay)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Deductions</span>
              <span className="font-medium text-red-600">-{formatCurrency(totalDeductions)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-semibold text-gray-900">Net Pay</span>
              <span className="font-bold text-lg text-primary">{formatCurrency(netPay)}</span>
            </div>
          </div>

          <Input
            label="Notes"
            placeholder="Additional notes"
            error={errors.notes?.message}
            {...register('notes')}
          />

          <button type="submit" className="hidden" />
        </form>
      </Modal>

      {/* View Payroll Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Payroll Details"
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>
        }
      >
        {selectedPayroll && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Payroll Number</p>
                <p className="font-medium">{selectedPayroll.payrollNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <Badge variant={getStatusVariant(selectedPayroll.status)}>
                  {selectedPayroll.status.replace('_', ' ')}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">User</p>
                <p className="font-medium">{selectedPayroll.userName}</p>
                <p className="text-xs text-gray-500">{selectedPayroll.userEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Period</p>
                <p className="font-medium">{selectedPayroll.payrollPeriod}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Basic Salary</span>
                <span className="font-medium">{formatCurrency(selectedPayroll.basicSalary)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Allowances</span>
                <span className="font-medium text-green-600">+{formatCurrency(selectedPayroll.totalAllowances)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Gross Pay</span>
                <span className="font-medium">{formatCurrency(selectedPayroll.grossPay)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Deductions</span>
                <span className="font-medium text-red-600">-{formatCurrency(selectedPayroll.totalDeductions)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-semibold text-gray-900">Net Pay</span>
                <span className="font-bold text-lg text-primary">{formatCurrency(selectedPayroll.netPay)}</span>
              </div>
            </div>

            {selectedPayroll.notes && (
              <div>
                <p className="text-sm text-gray-600">Notes</p>
                <p className="font-medium">{selectedPayroll.notes}</p>
              </div>
            )}

            <div className="border-t pt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Created By</p>
                <p className="font-medium">{selectedPayroll.createdByName || '-'}</p>
              </div>
              <div>
                <p className="text-gray-600">Created At</p>
                <p className="font-medium">{format(new Date(selectedPayroll.createdAt), 'MMM dd, yyyy HH:mm')}</p>
              </div>
              {selectedPayroll.approvedByName && (
                <>
                  <div>
                    <p className="text-gray-600">Approved By</p>
                    <p className="font-medium">{selectedPayroll.approvedByName}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Approved At</p>
                    <p className="font-medium">
                      {selectedPayroll.approvedAt ? format(new Date(selectedPayroll.approvedAt), 'MMM dd, yyyy HH:mm') : '-'}
                    </p>
                  </div>
                </>
              )}
              {selectedPayroll.paidByName && (
                <>
                  <div>
                    <p className="text-gray-600">Paid By</p>
                    <p className="font-medium">{selectedPayroll.paidByName}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Paid At</p>
                    <p className="font-medium">
                      {selectedPayroll.paidAt ? format(new Date(selectedPayroll.paidAt), 'MMM dd, yyyy HH:mm') : '-'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
